import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    ArrowLeft,
    ArrowRight,
    Loader2,
    Camera,
    Trash2,
    Check,
    ChevronRight,
    ChevronLeft,
    ClipboardCheck,
    CheckCircle2,
    AlertCircle,
    Eye,
    Pencil,
    X,
} from "lucide-react";
import api, { API, tokenStore } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { OPS } from "@/constants/testIds";
import { ResponsibleEmployees } from "./ResponsibleEmployees";

const AUTOSAVE_MS = 30 * 1000;

// -------------------------------------------------- helpers
function toBackendUrl(relative) {
    if (!relative) return "";
    const t = tokenStore.getAccess();
    // Photo URLs come as "/api/files/..." (relative). Prefix with API base and
    // pass the JWT via ?auth= because <img> can't send Authorization headers.
    const abs = `${API.replace(/\/api$/, "")}${relative}`;
    return t ? `${abs}?auth=${encodeURIComponent(t)}` : abs;
}

// -------------------------------------------------- main
export default function QCRoundWizard({ inspectionId, onClose }) {
    const { me, isSuperAdmin, canQcRoundAdmin } = useAuth();
    const { t, lang } = useI18n();

    const [round, setRound] = useState(null);
    const [sections, setSections] = useState([]);
    const [violations, setViolations] = useState([]);
    const [photos, setPhotos] = useState([]);
    const [step, setStep] = useState(0);          // 0..sections.length-1, then N = review
    const [notes, setNotes] = useState({});         // {section_key: text}
    const [noteSeverities, setNoteSeverities] = useState({});  // {section_key: severity}
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [confirmSubmit, setConfirmSubmit] = useState(false);
    const [lightboxPhoto, setLightboxPhoto] = useState(null);
    const dirtyRef = useRef(false);

    // HR employee roster for THIS round's branch — powers the المسؤول dropdown.
    // Fetched once per round; fail-soft (the endpoint never 5xx's — it returns
    // available=false so we render the free-text fallback).
    const [employees, setEmployees] = useState([]);
    const [employeesAvailable, setEmployeesAvailable] = useState(false);
    const [employeesLoaded, setEmployeesLoaded] = useState(false);

    const isDraft = round?.status === "draft";
    const isSelfOwner = round?.inspector_user_id === me?.user?.id;
    // Only the round's own inspector (or super_admin, who can edit any draft) may edit.
    const canEdit = isDraft && (isSuperAdmin || isSelfOwner);
    // Photo deletion (user decision 2026-06): super_admin and the QC manager
    // (`qc_full_control`) may remove any photo at any time; whoever UPLOADED a
    // photo may remove it while the round is still a DRAFT — the "took it by
    // mistake" case, from the phone or the browser.
    const canDeletePhoto = useCallback(
        (p) => !!p && (canQcRoundAdmin || (isDraft && p.uploaded_by === me?.user?.id)),
        [canQcRoundAdmin, isDraft, me?.user?.id],
    );

    // -------------------------------------------------- load
    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [r, secs] = await Promise.all([
                api.get(`/qc-inspection/inspections/${inspectionId}`),
                api.get("/qc-inspection/sections"),
            ]);
            setRound(r.data);
            setViolations(r.data.violations || []);
            setPhotos(r.data.photos || []);
            setNotes(r.data.section_notes || {});
            setNoteSeverities(r.data.section_note_severities || {});
            setSections(secs.data || []);
            // Submitted rounds ALWAYS open on the Review step so the inspector
            // (and anyone viewing) immediately sees the summary + photo strip.
            // Older rounds submitted via direct API may have current_section_index=0
            // — snapping to N (=review) matches the user's mental model that
            // "View" on a submitted round shows the completed report, not step 1.
            const secLen = (secs.data || []).length;
            const isSubmitted = r.data.status === "submitted";
            const savedIdx = Number(r.data.current_section_index || 0);
            setStep(isSubmitted ? secLen : Math.min(Math.max(0, savedIdx), secLen));
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error_generic"));
            onClose && onClose();
        } finally {
            setLoading(false);
        }
    }, [inspectionId, t, onClose]);

    useEffect(() => { load(); }, [load]);

    // -------------------------------------------------- HR roster fetch
    useEffect(() => {
        const branchId = round?.branch_id;
        if (!branchId) return;
        let cancelled = false;
        setEmployeesLoaded(false);
        (async () => {
            try {
                const res = await api.get("/qc-inspection/employees", { params: { branch_id: branchId } });
                if (cancelled) return;
                const rows = res.data?.employees || [];
                setEmployees(rows);
                setEmployeesAvailable(!!res.data?.available && rows.length > 0);
            } catch (e) {
                if (cancelled) return;
                // Endpoint is fail-soft server-side; a thrown error here means a
                // transport/auth issue — fall back to free-text, don't block.
                setEmployees([]);
                setEmployeesAvailable(false);
            } finally {
                if (!cancelled) setEmployeesLoaded(true);
            }
        })();
        return () => { cancelled = true; };
    }, [round?.branch_id]);

    // -------------------------------------------------- derived per-section state
    const totalSteps = sections.length;
    const currentSection = step < totalSteps ? sections[step] : null;
    const isReview = step >= totalSteps;

    const violationsBySection = useMemo(() => {
        const map = {};
        for (const v of violations) {
            (map[v.section_key] = map[v.section_key] || []).push(v);
        }
        return map;
    }, [violations]);

    const photosByViolation = useMemo(() => {
        const map = {};
        for (const p of photos) {
            if (p.violation_id) (map[p.violation_id] = map[p.violation_id] || []).push(p);
        }
        return map;
    }, [photos]);

    // Section photos: evidence for something that is NOT on the checklist —
    // attached to the section note, tied to no violation, never sent to HR.
    const photosBySection = useMemo(() => {
        const map = {};
        for (const p of photos) {
            if (!p.violation_id && p.section_key) {
                (map[p.section_key] = map[p.section_key] || []).push(p);
            }
        }
        return map;
    }, [photos]);

    // -------------------------------------------------- autosave
    const saveDraft = useCallback(async ({ next_index } = {}) => {
        if (!canEdit || !round) return;
        if (!dirtyRef.current && next_index === undefined) return;
        setSaving(true);
        try {
            const patch = {
                section_notes: notes,
                section_note_severities: noteSeverities,
                completed_sections: sections
                    .filter((s) => (violationsBySection[s.section_key]?.length ?? 0) > 0 || (notes[s.section_key] || "").trim() !== "")
                    .map((s) => s.section_key),
            };
            if (next_index !== undefined) patch.current_section_index = next_index;
            await api.patch(`/qc-inspection/inspections/${round.id}`, patch);
            dirtyRef.current = false;
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error_generic"));
        } finally {
            setSaving(false);
        }
    }, [canEdit, round, notes, noteSeverities, sections, violationsBySection, t]);

    useEffect(() => {
        if (!canEdit) return;
        const id = setInterval(() => { if (dirtyRef.current) saveDraft(); }, AUTOSAVE_MS);
        return () => clearInterval(id);
    }, [canEdit, saveDraft]);

    // Autosave on step change too.
    const goto = async (nextStep) => {
        if (canEdit) await saveDraft({ next_index: Math.min(nextStep, totalSteps) });
        setStep(nextStep);
    };

    const setNote = (key, val) => {
        setNotes((n) => ({ ...n, [key]: val }));
        dirtyRef.current = true;
    };

    const setNoteSeverity = (key, val) => {
        setNoteSeverities((s) => {
            const next = { ...s };
            if (val) next[key] = val; else delete next[key];
            return next;
        });
        dirtyRef.current = true;
    };

    // -------------------------------------------------- violations lifecycle
    const findViolation = (section_key, item) =>
        (violationsBySection[section_key] || []).find((v) => v.item === item);

    const toggleItem = async (section_key, item) => {
        if (!canEdit) return;
        const existing = findViolation(section_key, item);
        if (existing) {
            // Unchecking removes the violation entirely (and its photos are soft-deleted server-side).
            try {
                await api.delete(`/qc-inspection/inspections/${round.id}/violations/${existing.id}`);
                setViolations((vs) => vs.filter((v) => v.id !== existing.id));
                setPhotos((ps) => ps.filter((p) => p.violation_id !== existing.id));
            } catch (e) {
                toast.error(e?.response?.data?.detail || t("error_generic"));
            }
            return;
        }
        try {
            const r = await api.post(`/qc-inspection/inspections/${round.id}/violations`, {
                section_key, item, severity: "moderate",
            });
            setViolations((vs) => [...vs, r.data]);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error_generic"));
        }
    };

    // Per-violation request sequence. Every edit is applied to local state
    // IMMEDIATELY (so a fast second click builds on the first) and the server
    // response is only adopted when no newer edit is in flight — otherwise an
    // older response would overwrite the newer value.
    const violSeqRef = useRef({});

    const patchViolation = async (id, patch) => {
        if (!canEdit) return;
        const seq = (violSeqRef.current[id] || 0) + 1;
        violSeqRef.current[id] = seq;
        setViolations((vs) => vs.map((v) => (v.id === id ? { ...v, ...patch } : v)));
        try {
            const r = await api.patch(`/qc-inspection/inspections/${round.id}/violations/${id}`, patch);
            if (violSeqRef.current[id] !== seq) return;   // a newer edit is in flight
            setViolations((vs) => vs.map((v) => (v.id === id ? r.data : v)));
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error_generic"));
            // Re-sync from the server so the UI never keeps a value the round doesn't have.
            try {
                const fresh = await api.get(`/qc-inspection/inspections/${round.id}`);
                setViolations(fresh.data.violations || []);
            } catch (_) { /* keep the optimistic value; the toast already told the user */ }
        }
    };

    const uploadPhoto = async (violationId, file, { photoType = "before", sectionKey } = {}) => {
        if (!canEdit || !file) return;
        // ------ Watermark before upload (fail-closed) ------
        // Every QC evidence photo MUST carry the burned-in inspector / branch
        // / timestamp band before we send it to R2. If the burn-in fails for
        // any reason (odd format, canvas error, HEIC…) we abort — never
        // upload the raw file (spec §5).
        let watermarked;
        try {
            const { applyWatermark } = await import("@/lib/watermark");
            watermarked = await applyWatermark(file, {
                inspectorName: me?.user?.name,
                branchName: (lang === "ar" ? round?.branch_name_ar : round?.branch_name_en) || round?.branch_name_ar || "",
                inspectionId: round?.id || "",
            });
        } catch (e) {
            console.error("[QC watermark] failed", e);
            toast.error(t("qc_ins_watermark_failed"));
            return;
        }
        const fd = new FormData();
        fd.append("file", watermarked);
        fd.append("photo_type", photoType);
        if (violationId) fd.append("violation_id", violationId);
        if (sectionKey) fd.append("section_key", sectionKey);
        try {
            // A section photo needs its note to already exist server-side, and
            // notes are autosaved — flush them so the first photo isn't refused.
            if (photoType === "general") await saveDraft({ next_index: step });
            const r = await api.post(`/qc-inspection/inspections/${round.id}/photos`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setPhotos((ps) => [...ps, r.data]);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error_generic"));
        }
    };

    const deletePhoto = async (photoId) => {
        const target = photos.find((p) => p.id === photoId);
        if (!canDeletePhoto(target)) return;
        try {
            await api.delete(`/qc-inspection/inspections/${round.id}/photos/${photoId}`);
            setPhotos((ps) => ps.filter((p) => p.id !== photoId));
            toast.success(t("qc_ins_photo_deleted"));
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error_generic"));
        }
    };

    // -------------------------------------------------- submit
    const submit = async () => {
        if (!canEdit) return;
        setSubmitting(true);
        try {
            await saveDraft();
            const r = await api.post(`/qc-inspection/inspections/${round.id}/submit`);
            setRound(r.data);
            setConfirmSubmit(false);
            toast.success(t("qc_ins_round_submitted"));
            // Auto-return to QC hub after a short delay so the toast is visible
            // (item #4 in feedback pass 2026-07-22).
            setTimeout(() => { onClose && onClose(); }, 900);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error_generic"));
        } finally {
            setSubmitting(false);
        }
    };

    // -------------------------------------------------- navigation helpers
    const jumpToSection = useCallback((section_key) => {
        const idx = sections.findIndex((s) => s.section_key === section_key);
        if (idx >= 0) setStep(idx);
    }, [sections]);

    // -------------------------------------------------- render helpers
    if (loading || !round) {
        return (
            <div data-testid={OPS.qcIns.wizard.root} className="min-h-[60vh] flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-[color:var(--brand-orange)]" />
            </div>
        );
    }

    const branchName = lang === "ar" ? round.branch_name_ar : (round.branch_name_en || round.branch_name_ar);
    const progressPct = totalSteps > 0 ? Math.round((Math.min(step, totalSteps) / totalSteps) * 100) : 0;

    return (
        <div data-testid={OPS.qcIns.wizard.root} className="pb-24">
            {/* ---------------------- header ---------------------- */}
            <div className="sticky top-0 z-10 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 border-b border-[color:var(--border-soft)] surface backdrop-blur-sm">
                <div className="flex items-center gap-3">
                    <Button
                        data-testid={OPS.qcIns.wizard.closeBtn}
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                            // Flush pending notes / severities before leaving —
                            // otherwise anything typed since the last autosave
                            // (30 s or a step change) would be lost.
                            if (canEdit && dirtyRef.current) await saveDraft();
                            onClose && onClose();
                        }}
                        className="shrink-0 gap-1.5 px-2 sm:px-3"
                        aria-label={t("qc_ins_back_to_hub")}
                    >
                        {lang === "ar" ? <ArrowRight className="h-4 w-4" /> : <ArrowLeft className="h-4 w-4" />}
                        <span className="hidden sm:inline text-[12px] font-semibold">
                            {t("qc_ins_back_to_hub")}
                        </span>
                    </Button>
                    <div className="min-w-0 flex-1">
                        <h1 className="text-sm font-bold text-[color:var(--ink)] truncate">{branchName}</h1>
                        <p className="text-[11px] text-[color:var(--ink-muted)]">
                            {isReview
                                ? t("qc_ins_wizard_review")
                                : currentSection
                                    ? (lang === "ar" ? currentSection.title_ar : (currentSection.title_en || currentSection.title_ar))
                                    : ""}
                        </p>
                    </div>
                    <div className="shrink-0 flex items-center gap-2">
                        {round.status === "draft" ? (
                            <Badge variant="secondary" className="bg-[color:rgba(232,168,80,0.20)] text-[color:var(--brand-brown)] border-0 text-[10px]">
                                {t("qc_ins_status_draft")}
                            </Badge>
                        ) : (
                            <Badge variant="secondary" className="bg-[color:rgba(46,160,67,0.14)] text-[color:var(--success)] border-0 text-[10px]">
                                {t("qc_ins_status_submitted")}
                            </Badge>
                        )}
                        {saving ? (
                            <span
                                data-testid={OPS.qcIns.wizard.savingIndicator}
                                className="inline-flex items-center gap-1 text-[10px] text-[color:var(--ink-muted)]"
                            >
                                <Loader2 className="h-3 w-3 animate-spin" />
                                {t("qc_ins_autosaving")}
                            </span>
                        ) : null}
                    </div>
                </div>
                <div className="mt-2 flex items-center gap-2">
                    <Progress
                        data-testid={OPS.qcIns.wizard.progress}
                        value={progressPct}
                        className="h-1.5 flex-1"
                    />
                    <span className="text-[10px] font-medium text-[color:var(--ink-muted)] tnum">
                        {Math.min(step, totalSteps)} / {totalSteps}
                    </span>
                </div>
            </div>

            {/* ---------------------- body ---------------------- */}
            <div className="mt-4 space-y-4">
                {isReview ? (
                    <ReviewStep
                        round={round}
                        violations={violations}
                        photos={photos}
                        photosByViolation={photosByViolation}
                        notes={notes}
                        noteSeverities={noteSeverities}
                        sections={sections}
                        photosBySection={photosBySection}
                        canEdit={canEdit}
                        lang={lang}
                        t={t}
                        onJumpToSection={jumpToSection}
                        onOpenPhoto={setLightboxPhoto}
                    />
                ) : currentSection ? (
                    <SectionStep
                        section={currentSection}
                        violations={violationsBySection[currentSection.section_key] || []}
                        photosByViolation={photosByViolation}
                        sectionPhotos={photosBySection[currentSection.section_key] || []}
                        noteValue={notes[currentSection.section_key] || ""}
                        onNoteChange={(v) => setNote(currentSection.section_key, v)}
                        noteSeverity={noteSeverities[currentSection.section_key] || ""}
                        onNoteSeverityChange={(v) => setNoteSeverity(currentSection.section_key, v)}
                        onToggleItem={(item) => toggleItem(currentSection.section_key, item)}
                        onPatchViolation={patchViolation}
                        onUploadPhoto={uploadPhoto}
                        onDeletePhoto={deletePhoto}
                        onOpenPhoto={setLightboxPhoto}
                        canEdit={canEdit}
                        canDeletePhoto={canDeletePhoto}
                        employees={employees}
                        employeesAvailable={employeesAvailable}
                        employeesLoaded={employeesLoaded}
                        lang={lang}
                        t={t}
                    />
                ) : null}
            </div>

            {/* ---------------------- footer ---------------------- */}
            <div className="fixed bottom-0 inset-x-0 sm:inset-x-auto sm:right-0 sm:left-[var(--sidebar-w,0px)] z-20 border-t border-[color:var(--border-soft)] surface px-4 sm:px-6 py-3">
                <div className="flex items-center justify-between gap-2 max-w-5xl mx-auto">
                    <Button
                        variant="outline"
                        onClick={() => goto(step - 1)}
                        disabled={step === 0}
                        data-testid={OPS.qcIns.wizard.backBtn}
                    >
                        {lang === "ar" ? <ArrowRight className="h-4 w-4 me-2" /> : <ArrowLeft className="h-4 w-4 me-2" />}
                        {t("back")}
                    </Button>
                    {!isReview ? (
                        <Button
                            onClick={() => goto(step + 1)}
                            data-testid={OPS.qcIns.wizard.nextBtn}
                            className="bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white"
                        >
                            {step === totalSteps - 1 ? t("qc_ins_review") : t("next")}
                            {lang === "ar" ? <ChevronLeft className="h-4 w-4 ms-2" /> : <ChevronRight className="h-4 w-4 ms-2" />}
                        </Button>
                    ) : canEdit ? (
                        <Button
                            onClick={() => setConfirmSubmit(true)}
                            disabled={submitting}
                            data-testid={OPS.qcIns.wizard.submitBtn}
                            className="bg-[color:var(--success)] hover:bg-[color:var(--success)]/90 text-white"
                        >
                            {submitting ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Check className="h-4 w-4 me-2" />}
                            {t("qc_ins_submit_round")}
                        </Button>
                    ) : null}
                </div>
            </div>

            {/* ---------------------- submit confirm ---------------------- */}
            <AlertDialog open={confirmSubmit} onOpenChange={setConfirmSubmit}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("qc_ins_submit_confirm")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t("qc_ins_submit_confirm_desc")(violations.length)}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid={OPS.qcIns.wizard.submitCancel} disabled={submitting}>
                            {t("cancel")}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={submit}
                            disabled={submitting}
                            data-testid={OPS.qcIns.wizard.submitConfirm}
                            className="bg-[color:var(--success)] hover:bg-[color:var(--success)]/90 text-white"
                        >
                            {t("qc_ins_submit_round")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* ---------------------- photo lightbox ---------------------- */}
            <PhotoLightbox
                photo={lightboxPhoto}
                onClose={() => setLightboxPhoto(null)}
                t={t}
            />
        </div>
    );
}

// ==========================================================================
//                          SECTION STEP
// ==========================================================================
function SectionStep({
    section, violations, photosByViolation, sectionPhotos, noteValue, onNoteChange,
    noteSeverity, onNoteSeverityChange,
    onToggleItem, onPatchViolation, onUploadPhoto, onDeletePhoto, onOpenPhoto,
    canEdit, canDeletePhoto, employees, employeesAvailable, employeesLoaded, lang, t,
}) {
    const subtitle = lang === "ar" ? section.subtitle_ar : (section.subtitle_en || section.subtitle_ar);
    const violatedItems = new Set(violations.map((v) => v.item));

    return (
        <>
            {subtitle ? (
                <p data-testid={OPS.qcIns.wizard.sectionSubtitle} className="text-xs text-[color:var(--ink-muted)]">
                    {subtitle}
                </p>
            ) : null}

            <div className="text-[11px] text-[color:var(--ink-muted)] leading-relaxed rounded-lg bg-[color:rgba(186,217,235,0.15)] border border-[color:var(--border-soft)]/60 px-3 py-2">
                {t("qc_ins_check_only_violations")}
            </div>

            <div className="space-y-2">
                {section.items.map((item, i) => {
                    const violated = violatedItems.has(item);
                    const violation = violations.find((v) => v.item === item);
                    return (
                        <Card
                            key={i}
                            data-testid={OPS.qcIns.wizard.itemRow(section.section_key, i)}
                            className={`surface border-[color:var(--border-soft)] rounded-xl shadow-[var(--shadow-sm)] ${
                                violated ? "ring-1 ring-[color:var(--brand-orange)]/40" : ""
                            }`}
                        >
                            <CardHeader className="pb-2">
                                <div className="flex items-start gap-2">
                                    <Checkbox
                                        checked={violated}
                                        onCheckedChange={() => onToggleItem(item)}
                                        disabled={!canEdit}
                                        data-testid={OPS.qcIns.wizard.itemCheckbox(section.section_key, i)}
                                        className="mt-0.5"
                                    />
                                    <Label
                                        className={`flex-1 text-sm leading-snug cursor-pointer ${
                                            violated ? "text-[color:var(--ink)] font-semibold" : "text-[color:var(--ink)]"
                                        }`}
                                        onClick={() => canEdit && onToggleItem(item)}
                                    >
                                        <bdi>{item}</bdi>
                                    </Label>
                                </div>
                            </CardHeader>

                            {violated && violation ? (
                                <CardContent className="pt-0">
                                    <ViolationEditor
                                        violation={violation}
                                        photos={photosByViolation[violation.id] || []}
                                        onPatch={(patch) => onPatchViolation(violation.id, patch)}
                                        onUploadPhoto={(f) => onUploadPhoto(violation.id, f)}
                                        onDeletePhoto={onDeletePhoto}
                                        canEdit={canEdit}
                                        canDeletePhoto={canDeletePhoto}
                                        employees={employees}
                                        employeesAvailable={employeesAvailable}
                                        employeesLoaded={employeesLoaded}
                                        lang={lang}
                                        t={t}
                                    />
                                </CardContent>
                            ) : null}
                        </Card>
                    );
                })}
            </div>

            <div className="space-y-1.5 pt-2">
                <Label className="text-xs">{t("qc_ins_section_notes")}</Label>
                <Textarea
                    data-testid={OPS.qcIns.wizard.sectionNotes(section.section_key)}
                    value={noteValue}
                    onChange={(e) => onNoteChange(e.target.value)}
                    disabled={!canEdit}
                    placeholder={t("qc_ins_section_notes_ph")}
                    className="min-h-[80px]"
                />
                <SectionNoteSeverity
                    section={section}
                    value={noteSeverity}
                    onChange={onNoteSeverityChange}
                    hasNote={(noteValue || "").trim().length > 0}
                    canEdit={canEdit}
                    t={t}
                />
                <SectionPhotos
                    section={section}
                    photos={sectionPhotos || []}
                    noteValue={noteValue}
                    canEdit={canEdit}
                    canDeletePhoto={canDeletePhoto}
                    onUploadPhoto={onUploadPhoto}
                    onDeletePhoto={onDeletePhoto}
                    onOpenPhoto={onOpenPhoto}
                    t={t}
                />
            </div>
        </>
    );
}

// -------------------------------------------------- section note severity
const NOTE_SEV_NONE = "__none__";

function SectionNoteSeverity({ section, value, onChange, hasNote, canEdit, t }) {
    const tone = value === "critical" ? "destructive" : value === "moderate" ? "warning" : "secondary";
    if (!canEdit) {
        if (!value) return null;
        return (
            <div className="flex items-center gap-2 pt-1">
                <Label className="text-[11px] text-[color:var(--ink-muted)]">
                    {t("qc_ins_section_note_severity")}
                </Label>
                <Badge
                    data-testid={OPS.qcIns.wizard.sectionNoteSeverity(section.section_key)}
                    variant={tone}
                >
                    {t(`qc_ins_sev_${value}`)}
                </Badge>
            </div>
        );
    }
    return (
        <div className="pt-1 space-y-1">
            <Label className="text-[11px] text-[color:var(--ink-muted)]">
                {t("qc_ins_section_note_severity")}
            </Label>
            <Select
                value={value || NOTE_SEV_NONE}
                onValueChange={(v) => onChange(v === NOTE_SEV_NONE ? "" : v)}
                disabled={!hasNote}
            >
                <SelectTrigger
                    data-testid={OPS.qcIns.wizard.sectionNoteSeverity(section.section_key)}
                    className="h-9 text-sm max-w-[240px]"
                >
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value={NOTE_SEV_NONE}>{t("qc_ins_section_note_severity_none")}</SelectItem>
                    <SelectItem value="critical">{t("qc_ins_sev_critical")}</SelectItem>
                    <SelectItem value="moderate">{t("qc_ins_sev_moderate")}</SelectItem>
                    <SelectItem value="minor">{t("qc_ins_sev_minor")}</SelectItem>
                </SelectContent>
            </Select>
            {!hasNote ? (
                <p className="text-[10px] leading-snug text-[color:var(--ink-muted)]">
                    {t("qc_ins_section_note_severity_needs_note")}
                </p>
            ) : null}
        </div>
    );
}

// -------------------------------------------------- section (off-checklist) photos
function SectionPhotos({
    section, photos, noteValue, canEdit, canDeletePhoto,
    onUploadPhoto, onDeletePhoto, onOpenPhoto, t,
}) {
    const fileRef = useRef(null);
    const hasNote = (noteValue || "").trim().length > 0;

    const onFileChange = (e) => {
        const f = e.target.files?.[0];
        if (f) onUploadPhoto(null, f, { photoType: "general", sectionKey: section.section_key });
        e.target.value = "";
    };

    if (!canEdit && photos.length === 0) return null;

    return (
        <div data-testid={OPS.qcIns.wizard.sectionPhotos(section.section_key)} className="pt-1 space-y-1.5">
            <div className="flex items-center justify-between">
                <Label className="text-[11px] text-[color:var(--ink-muted)]">
                    {t("qc_ins_section_photos")}
                </Label>
                <span className="text-[10px] text-[color:var(--ink-muted)] tnum">{photos.length}</span>
            </div>
            <div className="flex flex-wrap gap-2">
                {photos.map((p) => (
                    <PhotoThumb
                        key={p.id}
                        photo={p}
                        canDelete={canDeletePhoto ? canDeletePhoto(p) : false}
                        onDelete={() => onDeletePhoto(p.id)}
                        onOpen={onOpenPhoto ? () => onOpenPhoto(p) : undefined}
                        t={t}
                    />
                ))}
                {canEdit ? (
                    <>
                        <input
                            ref={fileRef}
                            type="file"
                            accept="image/*"
                            capture="environment"
                            onChange={onFileChange}
                            className="hidden"
                            data-testid={OPS.qcIns.wizard.sectionPhotoInput(section.section_key)}
                        />
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={!hasNote}
                            data-testid={OPS.qcIns.wizard.sectionPhotoBtn(section.section_key)}
                            onClick={() => fileRef.current?.click()}
                            className="gap-1"
                        >
                            <Camera className="h-4 w-4" />
                            {t("qc_ins_section_photo_add")}
                        </Button>
                    </>
                ) : null}
            </div>
            {canEdit && !hasNote ? (
                <p
                    data-testid={OPS.qcIns.wizard.sectionPhotoHint(section.section_key)}
                    className="text-[10px] leading-snug text-[color:var(--ink-muted)]"
                >
                    {t("qc_ins_section_photo_needs_note")}
                </p>
            ) : null}
        </div>
    );
}

// -------------------------------------------------- violation editor
function ViolationEditor({ violation, photos, onPatch, onUploadPhoto, onDeletePhoto, canEdit, canDeletePhoto, employees, employeesAvailable, employeesLoaded, lang, t }) {
    const [description, setDescription] = useState(violation.description || "");
    const [deadline, setDeadline] = useState(violation.deadline || "");
    const fileRef = useRef(null);

    useEffect(() => {
        setDescription(violation.description || "");
        setDeadline(violation.deadline || "");
    }, [violation.id, violation.description, violation.deadline]);

    const commit = (patch) => onPatch(patch);

    const onTextBlur = (field, value) => {
        if (value === violation[field]) return;
        commit({ [field]: value });
    };

    const onFileChange = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // reset so the same file can be picked again
        if (file) await onUploadPhoto(file);
    };

    return (
        <div className="mt-2 space-y-3 rounded-lg surface-2 border border-[color:var(--border-soft)] p-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1.5">
                    <Label className="text-[11px]">{t("qc_ins_severity")}</Label>
                    <Select
                        value={violation.severity}
                        onValueChange={(v) => commit({ severity: v })}
                        disabled={!canEdit}
                    >
                        <SelectTrigger data-testid={OPS.qcIns.wizard.severitySelect(violation.id)} className="h-9 text-sm">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="critical">{t("qc_ins_sev_critical")}</SelectItem>
                            <SelectItem value="moderate">{t("qc_ins_sev_moderate")}</SelectItem>
                            <SelectItem value="minor">{t("qc_ins_sev_minor")}</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-1.5">
                    <Label className="text-[11px]">{t("qc_ins_responsible")}</Label>
                    <ResponsibleEmployees
                        value={violation.responsible_employees || []}
                        employees={employees}
                        employeesAvailable={employeesAvailable}
                        employeesLoaded={employeesLoaded}
                        canEdit={canEdit}
                        onChange={(rows) => commit({ responsible_employees: rows })}
                        prefix={violation.id}
                        t={t}
                    />
                </div>
                <div className="space-y-1.5">
                    <Label className="text-[11px]">{t("qc_ins_deadline")}</Label>
                    <Input
                        data-testid={OPS.qcIns.wizard.deadlineInput(violation.id)}
                        type="date"
                        value={deadline}
                        onChange={(e) => setDeadline(e.target.value)}
                        onBlur={() => commit({ deadline: deadline || null })}
                        disabled={!canEdit}
                        className="h-9 text-sm"
                        dir="ltr"
                    />
                </div>
            </div>

            <div className="space-y-1.5">
                <Label className="text-[11px]">{t("qc_ins_description")}</Label>
                <Textarea
                    data-testid={OPS.qcIns.wizard.descriptionInput(violation.id)}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    onBlur={() => onTextBlur("description", description)}
                    disabled={!canEdit}
                    className="min-h-[60px] text-sm"
                />
            </div>

            {/* Photos row */}
            <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                    <Label className="text-[11px]">{t("qc_ins_photos")}</Label>
                    <span className="text-[10px] text-[color:var(--ink-muted)]">{photos.length}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                    {photos.map((p) => (
                        <PhotoThumb
                            key={p.id}
                            photo={p}
                            canDelete={canDeletePhoto ? canDeletePhoto(p) : false}
                            onDelete={() => onDeletePhoto(p.id)}
                            t={t}
                        />
                    ))}
                    {canEdit ? (
                        <>
                            <input
                                ref={fileRef}
                                type="file"
                                accept="image/*"
                                capture="environment"
                                onChange={onFileChange}
                                className="hidden"
                                data-testid={OPS.qcIns.wizard.photoInput(violation.id)}
                            />
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                data-testid={OPS.qcIns.wizard.photoBtn(violation.id)}
                                onClick={() => fileRef.current?.click()}
                                className="gap-1"
                            >
                                <Camera className="h-4 w-4" />
                                {t("qc_ins_capture_photo")}
                            </Button>
                        </>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

// -------------------------------------------------- photo thumb
function PhotoThumb({ photo, canDelete, onDelete, onOpen, t }) {
    return (
        <div
            data-testid={OPS.qcIns.wizard.photoThumb(photo.id)}
            className="relative group rounded-lg overflow-hidden border border-[color:var(--border-soft)] bg-black/5"
            style={{ width: 72, height: 72 }}
        >
            {/* Image loads with ?auth=<jwt> because <img> can't send Bearer headers. */}
            <img
                src={toBackendUrl(photo.url)}
                alt={photo.original_filename || "photo"}
                loading="lazy"
                onClick={onOpen}
                className={`w-full h-full object-cover ${onOpen ? "cursor-zoom-in" : ""}`}
            />
            {canDelete ? (
                <button
                    type="button"
                    onClick={onDelete}
                    aria-label={t("qc_ins_delete_photo")}
                    data-testid={OPS.qcIns.wizard.photoDeleteBtn(photo.id)}
                    className="absolute top-0.5 right-0.5 rounded-full bg-black/60 hover:bg-[color:var(--danger)] text-white p-1 sm:p-0.5 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                >
                    <Trash2 className="h-3 w-3" />
                </button>
            ) : null}
        </div>
    );
}

// ==========================================================================
//                          REVIEW STEP
// ==========================================================================
function ReviewStep({
    round, violations, photos, photosByViolation, notes, noteSeverities, sections, photosBySection,
    canEdit, lang, t, onJumpToSection, onOpenPhoto,
}) {
    const isSubmitted = round.status === "submitted";
    const bySection = useMemo(() => {
        const m = {};
        for (const v of violations) (m[v.section_key] = m[v.section_key] || []).push(v);
        return m;
    }, [violations]);

    const secPhotos = photosBySection || {};
    // A section is worth showing when it carries a violation, a note, or a
    // section photo — a round can legitimately have notes/photos and no
    // violation at all.
    const visibleSections = sections.filter((s) =>
        (bySection[s.section_key]?.length ?? 0) > 0
        || (notes[s.section_key] || "").trim() !== ""
        || (secPhotos[s.section_key]?.length ?? 0) > 0);

    return (
        <div data-testid={OPS.qcIns.wizard.review} className="space-y-4">
            {isSubmitted ? (
                <div className="flex items-center gap-2 rounded-lg bg-[color:rgba(46,160,67,0.10)] border border-[color:var(--success)]/30 px-3 py-2 text-sm text-[color:var(--success)]">
                    <CheckCircle2 className="h-4 w-4" />
                    {t("qc_ins_round_readonly")}
                </div>
            ) : violations.length > 0 ? (
                <div className="rounded-lg bg-[color:rgba(186,217,235,0.15)] border border-[color:var(--border-soft)]/60 px-3 py-2 text-[11px] text-[color:var(--ink-muted)] leading-relaxed">
                    {t("qc_ins_review_tap_to_edit")}
                </div>
            ) : null}

            <Card className="surface border-[color:var(--border-soft)] rounded-xl">
                <CardHeader className="pb-2">
                    <h3 className="text-sm font-bold text-[color:var(--ink)] inline-flex items-center gap-1.5">
                        <ClipboardCheck className="h-4 w-4 text-[color:var(--brand-brown)]" />
                        {t("qc_ins_review_summary")}
                    </h3>
                </CardHeader>
                <CardContent className="pt-0">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <StatBox label={t("qc_ins_total_sections")} value={sections.length} />
                        <StatBox label={t("qc_ins_total_violations")} value={violations.length} tone={violations.length > 0 ? "warn" : "ok"} />
                        <StatBox label={t("qc_ins_total_photos")} value={photos.length} />
                        <StatBox label={t("qc_ins_notes_count")} value={Object.values(notes).filter((v) => (v || "").trim()).length} />
                    </div>
                </CardContent>
            </Card>

            {visibleSections.length === 0 ? (
                <div className="rounded-lg border border-dashed border-[color:var(--border-soft)] py-6 text-center text-xs text-[color:var(--ink-muted)]">
                    {t("qc_ins_no_violations_yet")}
                </div>
            ) : (
                <div className="space-y-3">
                    {visibleSections
                        .map((s) => (
                            <Card key={s.section_key} className="surface border-[color:var(--border-soft)] rounded-xl">
                                <CardHeader className="pb-2 flex-row items-start justify-between gap-2 space-y-0">
                                    <div className="min-w-0 flex-1">
                                        <h3 className="text-sm font-bold text-[color:var(--ink)]">
                                            {lang === "ar" ? s.title_ar : (s.title_en || s.title_ar)}
                                        </h3>
                                        {(notes[s.section_key] || "").trim() ? (
                                            <p className="text-[11px] text-[color:var(--ink-muted)] italic mt-1">
                                                <bdi>{notes[s.section_key]}</bdi>
                                            </p>
                                        ) : null}
                                        {(noteSeverities || {})[s.section_key] ? (
                                            <Badge
                                                data-testid={OPS.qcIns.wizard.sectionNoteSeverity(s.section_key)}
                                                variant={
                                                    noteSeverities[s.section_key] === "critical" ? "destructive"
                                                        : noteSeverities[s.section_key] === "moderate" ? "warning" : "secondary"
                                                }
                                                className="mt-1.5"
                                            >
                                                {t("qc_ins_section_note_severity")}: {t(`qc_ins_sev_${noteSeverities[s.section_key]}`)}
                                            </Badge>
                                        ) : null}
                                    </div>
                                    {canEdit ? (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            data-testid={OPS.qcIns.wizard.reviewEditSection(s.section_key)}
                                            onClick={() => onJumpToSection(s.section_key)}
                                            className="shrink-0 h-7 gap-1 text-[11px] text-[color:var(--brand-orange)] border-[color:var(--brand-orange)]/40 hover:bg-[color:var(--brand-orange)]/10"
                                        >
                                            <Pencil className="h-3 w-3" />
                                            {t("qc_ins_edit_section")}
                                        </Button>
                                    ) : null}
                                </CardHeader>
                                <CardContent className="pt-0 space-y-2">
                                    {(bySection[s.section_key] || []).map((v) => (
                                        <ReviewViolationRow
                                            key={v.id}
                                            v={v}
                                            photos={photosByViolation[v.id] || []}
                                            canEdit={canEdit}
                                            onJumpToSection={() => onJumpToSection(v.section_key)}
                                            onOpenPhoto={onOpenPhoto}
                                            t={t}
                                        />
                                    ))}
                                    {(secPhotos[s.section_key] || []).length > 0 ? (
                                        <div
                                            data-testid={OPS.qcIns.wizard.reviewSectionPhotos(s.section_key)}
                                            className="rounded-lg bg-[color:rgba(186,217,235,0.15)] p-2"
                                        >
                                            <div className="text-[10px] font-semibold text-[color:var(--ink-muted)] mb-1.5">
                                                {t("qc_ins_section_photos")}
                                            </div>
                                            <div className="flex flex-wrap gap-1.5">
                                                {(secPhotos[s.section_key] || []).map((p) => (
                                                    <button
                                                        key={p.id}
                                                        type="button"
                                                        data-testid={OPS.qcIns.wizard.reviewPhotoThumb(p.id)}
                                                        onClick={() => onOpenPhoto && onOpenPhoto(p)}
                                                        className="relative rounded-md overflow-hidden border border-[color:var(--border-soft)] bg-black/5 focus:outline-none focus:ring-2 focus:ring-[color:var(--brand-orange)]/60 transition-transform hover:scale-[1.03]"
                                                        style={{ width: 56, height: 56 }}
                                                        aria-label={t("qc_ins_review_photo_alt")}
                                                    >
                                                        <img
                                                            src={toBackendUrl(p.url)}
                                                            alt={p.original_filename || t("qc_ins_review_photo_alt")}
                                                            loading="lazy"
                                                            className="w-full h-full object-cover"
                                                        />
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    ) : null}
                                </CardContent>
                            </Card>
                        ))}
                </div>
            )}
        </div>
    );
}

function StatBox({ label, value, tone }) {
    return (
        <div className={`rounded-lg border border-[color:var(--border-soft)] p-3 text-center ${
            tone === "warn" ? "bg-[color:rgba(232,168,80,0.10)]" : ""
        }`}>
            <div className={`text-xl font-extrabold tnum ${tone === "warn" ? "text-[color:var(--brand-brown)]" : "text-[color:var(--ink)]"}`}>
                {value}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--ink-muted)] mt-1">
                {label}
            </div>
        </div>
    );
}

function ReviewViolationRow({ v, photos, canEdit, onJumpToSection, onOpenPhoto, t }) {
    const sevTone = v.severity === "critical" ? "danger" : v.severity === "moderate" ? "warn" : "muted";
    // The whole row is clickable when editable — jumps back to the section
    // so the inspector can change severity, deadline, add/remove photos, etc.
    const clickable = canEdit && typeof onJumpToSection === "function";
    return (
        <div
            data-testid={OPS.qcIns.wizard.reviewViolationCard(v.id)}
            role={clickable ? "button" : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={clickable ? onJumpToSection : undefined}
            onKeyDown={clickable ? (e) => {
                if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onJumpToSection(); }
            } : undefined}
            className={`flex items-start gap-2 rounded-lg bg-[color:rgba(224,222,217,0.28)] p-2 ${
                clickable ? "cursor-pointer hover:bg-[color:rgba(224,222,217,0.55)] transition-colors" : ""
            }`}
        >
            <AlertCircle className={`h-4 w-4 mt-0.5 shrink-0 ${
                sevTone === "danger" ? "text-[color:var(--danger)]"
                    : sevTone === "warn" ? "text-[color:var(--brand-orange)]"
                        : "text-[color:var(--ink-muted)]"
            }`} />
            <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-[color:var(--ink)]"><bdi>{v.item}</bdi></div>
                {v.description ? (
                    <div className="text-[11px] text-[color:var(--ink-muted)] mt-0.5">{v.description}</div>
                ) : null}
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[10px] text-[color:var(--ink-muted)]">
                    <span>{t(`qc_ins_sev_${v.severity}`)}</span>
                    {(v.responsible_employees || []).map((r, i) => (
                        <span key={`${r.employee_id || r.full_name}-${i}`}>
                            · <bdi>{r.full_name}</bdi>{!r.employee_id ? ` (${t("qc_ins_responsible_unverified")})` : ""}
                        </span>
                    ))}
                    {v.deadline ? <span>· <bdi dir="ltr">{v.deadline}</bdi></span> : null}
                </div>
                {/* Photo strip — tap any thumb to open the lightbox */}
                {photos && photos.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                        {photos.map((p) => (
                            <button
                                key={p.id}
                                type="button"
                                data-testid={OPS.qcIns.wizard.reviewPhotoThumb(p.id)}
                                onClick={(e) => { e.stopPropagation(); onOpenPhoto && onOpenPhoto(p); }}
                                className="relative rounded-md overflow-hidden border border-[color:var(--border-soft)] bg-black/5 focus:outline-none focus:ring-2 focus:ring-[color:var(--brand-orange)]/60 transition-transform hover:scale-[1.03]"
                                style={{ width: 56, height: 56 }}
                                aria-label={t("qc_ins_review_photo_alt")}
                            >
                                <img
                                    src={toBackendUrl(p.url)}
                                    alt={p.original_filename || t("qc_ins_review_photo_alt")}
                                    loading="lazy"
                                    className="w-full h-full object-cover"
                                />
                                <span className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 bg-black/30 transition-opacity">
                                    <Eye className="h-4 w-4 text-white" />
                                </span>
                            </button>
                        ))}
                    </div>
                ) : null}
            </div>
        </div>
    );
}


// ==========================================================================
//                          PHOTO LIGHTBOX
// ==========================================================================
function PhotoLightbox({ photo, onClose, t }) {
    // Close on Escape key.
    useEffect(() => {
        if (!photo) return undefined;
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [photo, onClose]);

    if (!photo) return null;
    return (
        <div
            data-testid={OPS.qcIns.wizard.lightbox}
            role="dialog"
            aria-modal="true"
            onClick={onClose}
            className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4"
        >
            <button
                type="button"
                data-testid={OPS.qcIns.wizard.lightboxClose}
                aria-label={t("qc_ins_lightbox_close")}
                onClick={onClose}
                className="absolute top-3 right-3 rounded-full bg-white/10 hover:bg-white/25 text-white p-2 backdrop-blur-sm transition-colors"
            >
                <X className="h-5 w-5" />
            </button>
            <img
                data-testid={OPS.qcIns.wizard.lightboxImage}
                src={toBackendUrl(photo.url)}
                alt={photo.original_filename || t("qc_ins_review_photo_alt")}
                onClick={(e) => e.stopPropagation()}
                className="max-w-full max-h-[92vh] object-contain rounded-md shadow-2xl"
            />
        </div>
    );
}
